'use client';

import { useCallback } from 'react';

import { useRouter } from 'next/navigation';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { useCurrentUserId, useLogin } from '@ps/ui/contexts/auth';

import { checkoutUrlBuilder } from '../../../checkout/builders';
import { useSubscribeModal } from '../../../subscribe/contexts/subscribe-modal/useSubscribeModal';

export interface PostUnlockActions {
  /** Open the subscribe flow preselected at the post's required tier. */
  onSubscribe: (level: PopStoreExtendedSubscriptionLevel | undefined) => void;
  /** Launch the pay-to-view (one-time purchase) checkout for the post. */
  onPay: () => void;
  /** Launch the tip-to-unlock flow for the post. */
  onTip: () => void;
}

export interface PostUnlockActionsInput {
  /** The post being unlocked. */
  postId: string;
  /** Creator public url code (`c` param on the pay/tip checkout URL). */
  urlCode: string;
  /** Creator store id — the pay/tip checkout scope + the subscribe-modal target. */
  sellerId: string;
  /**
   * The surface's bio context, or `null` on the non-1:1 DM inbox (mixed
   * creators, no single creator scope). Present → subscribe navigates to the
   * shareable `/<urlCode>/subscribe` route; `null` → subscribe opens the in-place
   * `SubscribeModalProvider` dialog (the route can't be used off a bio surface).
   */
  bioContext: BioContext | null;
  /**
   * Where checkout returns on success (`redirect_to`). Defaults to undefined,
   * which dismisses back to the launching surface (e.g. the chat) with the gate
   * lifted; the full post view passes the post permalink so "Done" lands there.
   */
  redirectTo?: string;
}

/**
 * The unlock LAUNCHERS for a gated post — subscribe / pay / tip. Each starts a
 * checkout as a SOFT nav (the launching surface stays mounted underneath), so
 * the completion is observed via {@link useOnCheckoutSuccess}, not a return
 * value. Anonymous viewers are routed through login first (subscribe + pay both
 * need a user), resuming the intended action via the post-login callback —
 * mirrors the clip paywall (`ClipAccessGate` / `BuyNowAction`).
 *
 * Split from {@link usePostUnlock} (which also re-resolves the post + toasts) so
 * the chat-bubble card can wire LIVE paywall buttons without dragging in the
 * un-redaction fetch — the card only needs to launch the flow.
 */
export function usePostUnlockActions({
  postId,
  urlCode,
  sellerId,
  bioContext,
  redirectTo,
}: PostUnlockActionsInput): PostUnlockActions {
  const router = useRouter();
  const login = useLogin();
  const subscribeModal = useSubscribeModal();
  // Viewer id from the `AuthProvider` (firebase-free at the static import level —
  // the provider dynamic-imports the SDK), so this hook keeps firebase/auth off
  // the visitor bio's first-load bundle. `undefined` while auth resolves; a tap
  // only fires post-hydration, by when it's the resolved uid or `null`.
  const userId = useCurrentUserId();

  const withUser = useCallback(
    (action: () => void) => {
      // Auth loads lazily — `userId` is `undefined` until it resolves. Ignore a
      // tap made before then, else a signed-in viewer would be misread as
      // logged-out and routed to /login. The re-tap once resolved proceeds.
      if (userId === undefined) return;
      if (userId !== null) {
        action();
        return;
      }
      // Restricted-post unlock → open the login modal on "Community Member"
      // (fan-facing), not the default "Creator or Business".
      login({ onSuccess: action });
    },
    [login, userId],
  );

  const onSubscribe = useCallback(
    (level: PopStoreExtendedSubscriptionLevel | undefined) => {
      withUser(() => {
        // A well-established bio context → the shareable subscribe ROUTE.
        if (bioContext != null) {
          router.push(
            bioUrl(bioContext).subscribe({
              defaultLevel: level,
              minLevel: level,
              redirectTo,
            }),
          );
          return;
        }
        // No bio context (non-1:1 DM inbox) → the in-place picker; the
        // `/<urlCode>/subscribe` route lives only under `(bio)/[url_code]`.
        if (subscribeModal == null) {
          console.error(
            'usePostUnlockActions: subscribe requested with no bioContext and no SubscribeModalProvider mounted.',
          );
          return;
        }
        subscribeModal.openSubscribe({
          creatorId: sellerId,
          defaultLevel: level,
          minLevel: level,
        });
      });
    },
    [withUser, router, bioContext, subscribeModal, sellerId, redirectTo],
  );

  const onPay = useCallback(() => {
    withUser(() => {
      const builder = checkoutUrlBuilder().post(p =>
        p.urlCode(urlCode).postId(postId),
      );
      if (redirectTo) builder.onSuccess(s => s.redirectTo(redirectTo));
      router.push(builder.url());
    });
  }, [withUser, router, urlCode, postId, redirectTo]);

  const onTip = useCallback(() => {
    withUser(() => {
      const builder = checkoutUrlBuilder().tip(t =>
        t.urlCode(urlCode).post(postId),
      );
      if (redirectTo) builder.onSuccess(s => s.redirectTo(redirectTo));
      router.push(builder.url());
    });
  }, [withUser, router, urlCode, postId, redirectTo]);

  return { onSubscribe, onPay, onTip };
}
